<script setup>
import { ref, onMounted, watch } from "vue";
import axios from "@/utils/axios";
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";
import { useLoading } from "vue-loading-overlay";
import loaderData from "@/utils/loader-data";
import { useRouter } from "vue-router";

const router = useRouter();
const route = useRoute("tasks-id");
const $loading = useLoading(loaderData);

const taskData = ref({
    title: "",
    description: "",
    type: "manual",
    workflow_id: null,
    workflow_step_id: null
});

const users = ref([]);
const userSearchQuery = ref("");
const selectedUser = ref(null);

const submitBtn = ref(1);

// Get users for assignments
const getUsers = () => {
    axios
        .get(`/get-all-users?search_query=${userSearchQuery.value}`)
        .then(async (response) => {
            if (response.status == "success") {
                users.value = Object.entries(response.data.users).map(
                    ([index, value]) => {
                        return {
                            value: value.id,
                            title: `${value.first_name} ${value.last_name}`,
                        };
                    }
                );
            }
        });
};

// Watch for changes in search query
watch(userSearchQuery, () => {
    getUsers();
});

// Load task data for editing
const loadTaskData = () => {
    let id = route.params.id;
    let loader = $loading.show();
    axios
        .get(`/tasks/${id}`)
        .then((response) => {
            if (response.status == "success") {
                const task = response.data.task;
                
                // Set base task data
                taskData.value = {
                    title: task.title,
                    description: task.description,
                    type: "manual",
                    workflow_id: task.workflow_id,
                    workflow_step_id: task.workflow_step_id
                };
                
                if (task.assignments && task.assignments.length > 0) {
                    selectedUser.value = task.assignments[0].user_id;
                    getUsers();
                }
            } else {
                toast.error(response.message, {
                    autoClose: 2000,
                });
            }
        })
        .catch((error) => {
            toast.error("Failed to load task data", {
                autoClose: 2000,
            });
        })
        .finally(() => {
            loader.hide();
        });
};

// Submit the task update
const updateTask = async () => {
    if (!taskData.value.title) {
        toast.error('Title is required', { autoClose: 2000 });
        return;
    }
    
    if (!selectedUser.value) {
        toast.error('Please select a user to assign', { autoClose: 2000 });
        return;
    }
    
    let loader = $loading.show();
    submitBtn.value = 0;
    
    // Prepare the data for submission
    const dataToSubmit = {
        title: taskData.value.title,
        description: taskData.value.description,
        type: taskData.value.type,
        assignments: [{ user_id: selectedUser.value, status: 'pending' }],
        workflow_id: taskData.value.workflow_id || null,
        workflow_step_id: taskData.value.workflow_step_id || null,
    };
    
    let id = route.params.id;
    axios
        .put(`/tasks/${id}`, dataToSubmit)
        .then((response) => {
            if (response.status == "success") {
                toast.success(response.message, {
                    autoClose: 2000,
                });
                router.push({ name: "tasks-list" });
            } else {
                toast.error(response.message, {
                    autoClose: 2000,
                });
            }
        })
        .catch((error) => {
            console.error(error);
            toast.error('An error occurred while updating the task', {
                autoClose: 2000,
            });
        })
        .finally(() => {
            loader.hide();
            submitBtn.value = 1;
        });
};

// Cancel task editing and return to list
const cancel = () => {
    router.push({ name: "tasks-list" });
};


const workflowSearchQuery = ref('');

const workflows = ref([]);
const workflowSteps = ref({});
watch(workflowSearchQuery, () => {
    fetchWorkflows();
});

const fetchWorkflows = () => {
    axios
        .get(`/workflows?all_workflows=true&search_query=${workflowSearchQuery.value}&sort_by=id&order_by=desc`)
        .then((response) => {
            if (response.status == "success") {
                workflows.value = response.data.workflows.data;
                fetchWorkflowSteps();
            }
        });
};

const fetchWorkflowSteps = () => {
    workflows.value.forEach(workflow => {
        axios
            .get(`/workflow_steps?workflow_id=${workflow.id}`)
            .then((response) => {
                if (response.status == "success") {
                    workflowSteps.value[workflow.id] = response.data.workflow_steps;
                }
            });
    });
};

// Initialize page
onMounted(() => {
    getUsers();
    loadTaskData();
    fetchWorkflows();
});

definePage({ meta: { requiresAuth: true } });
</script>

<template>
  <div>

    <div class="d-flex justify-end gap-1 mb-4">
      <VBtn variant="tonal" color="light" @click="cancel"> Discard </VBtn>
      <VBtn
        variant="tonal"
        color="primary"
        :loading="submitBtn === 0"
        @click="updateTask"
      >
        Update Task
      </VBtn>
    </div>


    <VCard title="Edit Task">
      <VCardText>
        <VRow>
          <VCol cols="12">
            <VTextField
              v-model="taskData.title"
              label="Task Title"
              required
              hint="Enter a descriptive title for the task"
            />
          </VCol>
          <VCol cols="12">
            <VTextarea
              v-model="taskData.description"
              label="Description"
              hint="Provide details about what this task involves"
              rows="3"
            />
          </VCol>
          <VCol cols="12" md="6">
              <AppAutocomplete
                  v-model:search="workflowSearchQuery"
                  v-model="taskData.workflow_id"
                  :items="workflows.map(w => ({title: w.id + ' - ' + w.title, value: w.id}))"
                  clearable
                  placeholder="Select workflow"
              />
          </VCol>
          <VCol cols="12" md="6" v-if="taskData.workflow_id" >
              <AppSelect
                  v-model="taskData.workflow_step_id"
                  :items="(workflowSteps[taskData.workflow_id] || []).map(s => ({title: s.title, value: s.id}))"
                  clearable
                  placeholder="Select workflow step"
              />
          </VCol>
        </VRow>
        
        <VDivider class="my-4" />
        
        <div class="d-flex flex-wrap justify-start justify-sm-space-between gap-y-4 gap-x-6 mb-6">
          <div class="d-flex flex-column justify-center">
            <h3 class="mb-0">Task Assignment</h3>
                        <span class="text-caption">Assign this task to a user</span>
          </div>
        </div>
        
        <VCard class="mb-6 pa-4">
                    <VRow>
            <VCol cols="12" md="6">
              <VAutocomplete
                v-model="selectedUser"
                :items="users"
                v-model:search-input="userSearchQuery"
                label="Select User"
                clearable
              />
            </VCol>
          </VRow>
        </VCard>
      </VCardText>
      
    </VCard>
  </div>
</template>

<style scoped>
.assignment-container:hover {
  background-color: rgba(var(--v-theme-primary), 0.05);
}
</style>
